import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import CloseIcon from '@mui/icons-material/Close';
import Popper from '@mui/material/Popper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
// import TextField from '@mui/material/TextField';
import Input from '@mui/material/Input';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import Avatar from '@mui/material/Avatar';
import { uniq } from 'lodash';
import { FilterOption, Filter, FilterType, Field, Operation } from '../types';
import {
  getFiltersConf,
  FilterConf,
  getMultipleValues,
  GetValueProps,
  getLeagueImage,
} from '../utils';
import { useStringsContext } from '../../../../contexts/strings';
import { StringType } from '../../../../sv-strings';
import { Divider, TextField, Typography } from '@mui/material';

function FilterButton(props: {
  fieldKey: keyof Filter;
  options: string[];
  filter: Filter;
  updateFilter: (updatedFilter: Partial<Filter>) => void;
}) {
  const { fieldKey, updateFilter, filter, options } = props;
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const [open, setOpen] = React.useState(false);
  const { strings } = useStringsContext();

  const getLabelKey = (key: string) => {
    if (fieldKey === 'operation' && filter.filterType === FilterType.SingleValue) {
      return (key.toUpperCase() + '_NULL') as keyof StringType['allPlayers'];
    } else if (fieldKey === 'operation') {
      return key.toUpperCase() as keyof StringType['allPlayers'];
    }

    return key as keyof StringType['allPlayers'];
  };
  const stringKey = getLabelKey(filter[fieldKey] as string);
  const buttonLabel = strings.allPlayers[stringKey] || filter[fieldKey];

  return (
    <>
      <Button
        ref={anchorRef}
        onClick={() => {
          if (options.length > 1) {
            setOpen((prevOpen) => !prevOpen);
          }
        }}
        sx={{ textTransform: 'none' }}
      >
        <span style={{ opacity: fieldKey === 'operation' ? 0.6 : 1 }}>{buttonLabel}</span>
      </Button>
      <Popper
        sx={{ zIndex: 10 }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener
                onClickAway={() => {
                  setOpen(false);
                }}
              >
                <MenuList>
                  {options.map((option) => (
                    <MenuItem
                      key={option}
                      disabled={option === filter[fieldKey]}
                      selected={option === filter[fieldKey]}
                      onClick={() => {
                        updateFilter({
                          [fieldKey]: option,
                        });
                        setOpen(false);
                      }}
                    >
                      {strings.allPlayers[getLabelKey(option)]}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}

export function FilterValueOptions(props: {
  options: FilterOption[];
  operation?: Operation;
  selectedOptions: string[];
  onSelect: (selections: string[], operation: Operation) => void;
  showImage: boolean;
  getValue: (params: GetValueProps) => string[];
}) {
  const { options, selectedOptions, onSelect, showImage, operation, getValue } = props;

  const [search, setSearch] = React.useState<string>('');
  const [contentSearch, setContentSearch] = React.useState<string>(
    (() => {
      if (!options.length) {
        return selectedOptions?.[0] || '';
      }

      return '';
    })()
  );
  const { strings } = useStringsContext();
  const hasSearch = options.length > 9;
  const hasDifferentOperation = uniq(options.map((o) => o.operation)).length > 1;
  return (
    <>
      {hasSearch ? (
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          size="small"
          placeholder={`${strings.allPlayers.filter}...`}
          sx={{ px: 2, py: 1, minWidth: '300px' }}
          onKeyDown={(e) => {
            e.stopPropagation();
          }}
        />
      ) : null}
      <Paper sx={{ maxHeight: 330, overflow: 'auto' }}>
        {options.length ? (
          <MenuList sx={{ p: 0 }}>
            {options
              .filter((option) =>
                hasSearch ? option.value.toUpperCase().includes(search.toUpperCase()) : true
              )
              .map((option) => {
                const isSelected =
                  selectedOptions.includes(option.value) &&
                  (hasDifferentOperation ? option.operation === operation : true);

                return (
                  <MenuItem
                    key={option.value + option.operation}
                    selected={isSelected}
                    onClick={() => {
                      onSelect(
                        getValue({
                          isSelected,
                          selectedOptions,
                          option,
                        }),
                        hasDifferentOperation ? option.operation : operation || option.operation
                      );
                    }}
                    sx={{ alignItems: 'flex-start', gap: 2, width: '100%' }}
                  >
                    {isSelected ? (
                      <CheckCircleOutlineIcon sx={{ fontSize: 16, mt: 0.8 }} />
                    ) : (
                      <RadioButtonUncheckedIcon sx={{ fontSize: 16, mt: 0.8 }} />
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        {showImage ? (
                          <>
                            {option.image ? (
                              <img src={option.image} height={24} />
                            ) : (
                              <Avatar sx={{ width: 24, height: 24 }}> </Avatar>
                            )}
                          </>
                        ) : null}

                        <Box
                          sx={{
                            textTransform: 'capitalize',
                            display: 'block',
                          }}
                        >
                          {option.label}
                        </Box>
                      </Box>

                      {option.subLabel ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 0.5 }}>
                          {showImage && option.subImage ? (
                            <img src={option.subImage} height={18} />
                          ) : null}
                          <Typography
                            variant="caption"
                            style={{
                              textTransform: 'capitalize',
                            }}
                          >
                            {option.subLabel}
                          </Typography>
                        </Box>
                      ) : null}
                    </Box>
                  </MenuItem>
                );
              })}
          </MenuList>
        ) : (
          <Box sx={{ p: 5 }}>
            <TextField
              value={contentSearch}
              onChange={(e) => setContentSearch(e.target.value)}
              fullWidth
              size="small"
              placeholder={`${strings.allPlayers.searchPlaceholderText}`}
              sx={{ minWidth: '300px', mb: 2 }}
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
              variant="outlined"
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => {
                  onSelect([contentSearch], operation || Operation.CS);
                }}
                variant="contained"
              >
                {strings.allPlayers.searchButton}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </>
  );
}

function FilterValue(props: {
  filter: Filter;
  updateFilter: (updatedFilter: Partial<Filter>) => void;
  filterConf?: FilterConf;
}) {
  const { updateFilter, filter, filterConf } = props;
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const [open, setOpen] = React.useState(false);
  const { strings } = useStringsContext();

  const options = filterConf?.options || [];

  const showImage = filterConf?.showImage || false;
  const getValue = filterConf?.getValue || getMultipleValues;

  function getValueLabel(option?: FilterOption, showAllSubLabel?: boolean) {
    const label = option?.label || '-';
    const image = option?.image;
    const subLabel = option?.subLabel;
    const subImage = option?.subImage;
    const extraInfo = (() => {
      if (!subLabel) {
        return '';
      }
      if (!showAllSubLabel && subImage) {
        return <img src={subImage} alt={subLabel} width={18} />;
      }
      if (!showAllSubLabel && !subImage) {
        return `(${subLabel[0].toUpperCase()})`;
      }

      return subLabel;
    })();
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
        {extraInfo}
        {image && !subImage ? <img src={image} alt={label} width={18} /> : null}
        {label}
      </Box>
    );
  }
  const label = (() => {
    if (filter.value.length === 0) {
      return '';
    }

    const filterConfOptions = filterConf?.options || [];

    const [firstValue] = filter.value;
    if (filter.value.length === 1 && filter.filterType === FilterType.SingleValue) {
      const selectedOption = filterConfOptions.find((f) => f.value === firstValue);
      return getValueLabel(selectedOption, true);
    }

    if (filter.value.length === 1 && firstValue && filter.field === Field.SearchContent) {
      return `'${firstValue}'`;
    }

    if (filter.value.length > 2) {
      return `${filter.value.length} ${strings.allPlayers.selectedItems}`;
    }
    const selectedOptions = filterConfOptions.filter((f) => {
      return filter.value.includes(f.value);
    });
    return selectedOptions.reduce((acc: any[], option, index) => {
      const rows = [getValueLabel(option, false)];
      if (index + 1 < selectedOptions.length) {
        rows.push(<Divider orientation="vertical" flexItem sx={{ mx: 1 }} />);
      }
      return [...acc, ...rows];
    }, []);
  })();

  return (
    <>
      <Button
        ref={anchorRef}
        onClick={() => {
          if (filter.filterType !== FilterType.SingleValue) {
            setOpen((prevOpen) => !prevOpen);
          }
        }}
        sx={{ textTransform: 'none', gap: 0.8 }}
      >
        {label}
      </Button>
      <Popper
        sx={{ zIndex: 10 }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener
                onClickAway={() => {
                  setOpen(false);
                }}
              >
                <Box>
                  <FilterValueOptions
                    options={options}
                    selectedOptions={filter.value}
                    showImage={showImage}
                    operation={filter.operation}
                    onSelect={(value, operation) => {
                      updateFilter({
                        value,
                        operation,
                      });
                      if (filter.field === Field.SearchContent) {
                        setOpen(false);
                      }
                    }}
                    getValue={getValue}
                  />
                </Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}

export default function FilterItemUI(props: {
  filter: Filter;
  removeFilter: () => void;
  updateFilter: (updatedFilter: Partial<Filter>) => void;
  leagues: FilterOption[];
  teams: FilterOption[];
}) {
  const { filter, removeFilter, updateFilter, leagues, teams } = props;

  const { strings } = useStringsContext();
  const filtersConf = getFiltersConf({
    strings,
    leagues,
    teams,
  });
  const filterConf = filtersConf.find((f) => f.field === filter.field);

  const buttonLabel = (() => {
    if (filter.field === Field.SearchContent) {
      return strings.allPlayers.searchContentLabel;
    }

    return strings.allPlayers[filter.field as keyof StringType['allPlayers']] || filter.field;
  })();
  return (
    <ButtonGroup variant="outlined" size="small">
      <Button
        sx={{
          textTransform: 'none',
        }}
      >
        {buttonLabel}
      </Button>
      <FilterButton
        filter={filter}
        updateFilter={updateFilter}
        fieldKey="operation"
        options={filterConf?.operations || []}
      />
      <FilterValue filter={filter} updateFilter={updateFilter} filterConf={filterConf} />
      <Button onClick={() => removeFilter()}>
        <CloseIcon sx={{ fontSize: 9 }} />
      </Button>
    </ButtonGroup>
  );
}
